import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./resizableColumns.module.scss";

const MIN_WIDTH = 72;
const MAX_WIDTH = 900;
const KEY_STEP = 16;
const FALLBACK_WIDTH = 200;
// The flex column gives up its spare width to the others down to this.
const FLEX_MIN_WIDTH = 160;

const clampWidth = (width) =>
  Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(width)));

function readStoredWidths(storageKey) {
  if (!storageKey) return null;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) || "null");
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (e) {
    return null;
  }
}

function writeStoredWidths(storageKey, widths) {
  if (!storageKey) return;
  try {
    if (widths) window.localStorage.setItem(storageKey, JSON.stringify(widths));
    else window.localStorage.removeItem(storageKey);
  } catch (e) {
    // Storage blocked (private mode): widths simply reset on reload.
  }
}

// Rendered widths of every resizable header cell in the row, by column key.
function measureRow(th) {
  const measured = {};
  th.parentElement.querySelectorAll("th[data-resize-key]").forEach((cell) => {
    measured[cell.getAttribute("data-resize-key")] = cell.getBoundingClientRect().width;
  });
  return measured;
}

// antd rebuilds the header cells when the table switches to the fixed layout,
// so the drag itself is tracked by the hook on the window, not by the cell.
function ResizableHeaderCell({
  resizeKey,
  resizeLabel,
  resizeActive,
  resizeFocusRef,
  onResizePointerDown,
  onResizeKeyDown,
  onResizeReset,
  shouldIgnoreClick,
  onClick,
  children,
  ...rest
}) {
  if (!resizeKey) {
    return (
      <th onClick={onClick} {...rest}>
        {children}
      </th>
    );
  }

  const handleCellClick = (event) => {
    // The click that ends a drag must not toggle the column's sorting.
    if (shouldIgnoreClick()) return;
    if (onClick) onClick(event);
  };

  return (
    <th onClick={handleCellClick} {...rest}>
      {children}
      <span
        ref={(el) => {
          if (el && resizeFocusRef.current === resizeKey && document.activeElement !== el) {
            el.focus();
          }
        }}
        role="separator"
        aria-orientation="vertical"
        aria-label={resizeLabel}
        title={resizeLabel}
        tabIndex={0}
        className={`${styles.handle}${resizeActive ? ` ${styles.active}` : ""}`}
        onPointerDown={(event) => onResizePointerDown(event, resizeKey)}
        onKeyDown={(event) => onResizeKeyDown(event, resizeKey)}
        onClick={(event) => event.stopPropagation()}
        onDoubleClick={(event) => {
          event.stopPropagation();
          onResizeReset();
        }}
      />
    </th>
  );
}

/**
 * Lets people drag column edges of an antd Table, like a spreadsheet.
 *
 * Until the first drag the table keeps its normal automatic layout. The first
 * drag freezes every column at its rendered width and switches to a fixed
 * layout. The `flexKey` column (the last one unless named) then takes up the
 * remaining space, never narrower than its own saved width (at most 160px
 * until someone drags it), so the table
 * still fills its card and scrolls sideways once the columns outgrow it.
 * Widths are kept per browser under `storageKey`; double-clicking an edge
 * restores the original layout.
 */
export default function useResizableColumns(
  columns,
  { storageKey, resizeLabel, flexKey } = {}
) {
  const [widths, setWidths] = useState(() => readStoredWidths(storageKey));
  const [activeKey, setActiveKey] = useState(null);
  const widthsRef = useRef(widths);
  widthsRef.current = widths;
  const ignoreClickRef = useRef(false);
  const focusKeyRef = useRef(null);
  const stopDragRef = useRef(null);

  useEffect(() => () => stopDragRef.current && stopDragRef.current(), []);

  const lastColumn = columns[columns.length - 1];
  const flexColumnKey = flexKey || (lastColumn && (lastColumn.key || lastColumn.dataIndex));

  const freeze = useCallback(
    (measured) => {
      setWidths((prev) => {
        if (prev) return prev;
        const frozen = {};
        Object.keys(measured).forEach((key) => {
          const width =
            key === flexColumnKey ? Math.min(measured[key], FLEX_MIN_WIDTH) : measured[key];
          frozen[key] = clampWidth(width);
        });
        return frozen;
      });
    },
    [flexColumnKey]
  );

  const setWidth = useCallback((key, width) => {
    setWidths((prev) => ({ ...(prev || {}), [key]: clampWidth(width) }));
  }, []);

  const persist = useCallback(() => {
    // Read after React applied the last change.
    setTimeout(() => writeStoredWidths(storageKey, widthsRef.current), 0);
  }, [storageKey]);

  const handlePointerDown = useCallback(
    (event, key) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      const measured = measureRow(event.currentTarget.parentElement);
      const startX = event.clientX;
      const startWidth = clampWidth(measured[key]);
      freeze(measured);
      setActiveKey(key);
      const previousCursor = document.body.style.cursor;
      const previousSelect = document.body.style.userSelect;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const move = (moveEvent) => setWidth(key, startWidth + moveEvent.clientX - startX);
      const stop = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", stop);
        window.removeEventListener("pointercancel", stop);
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousSelect;
        stopDragRef.current = null;
        setActiveKey(null);
        ignoreClickRef.current = true;
        setTimeout(() => {
          ignoreClickRef.current = false;
        }, 0);
        persist();
      };
      stopDragRef.current = stop;
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", stop);
      window.addEventListener("pointercancel", stop);
    },
    [freeze, setWidth, persist]
  );

  const handleKeyDown = useCallback(
    (event, key) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      event.stopPropagation();
      const measured = measureRow(event.currentTarget.parentElement);
      const step = event.key === "ArrowRight" ? KEY_STEP : -KEY_STEP;
      const current = (widthsRef.current && widthsRef.current[key]) || clampWidth(measured[key]);
      freeze(measured);
      setWidth(key, current + step);
      // Keep keyboard focus on this edge when antd rebuilds the header.
      focusKeyRef.current = key;
      setTimeout(() => {
        focusKeyRef.current = null;
      }, 0);
      persist();
    },
    [freeze, setWidth, persist]
  );

  const reset = useCallback(() => {
    setWidths(null);
    writeStoredWidths(storageKey, null);
  }, [storageKey]);

  const shouldIgnoreClick = useCallback(() => ignoreClickRef.current, []);

  let scrollX = 0;
  const resizableColumns = columns.map((col) => {
    const key = col.key || col.dataIndex;
    const width = widths ? widths[key] || col.width || FALLBACK_WIDTH : col.width;
    scrollX += width || FALLBACK_WIDTH;
    return {
      ...col,
      // In the fixed layout the flex column has no width of its own, so it
      // absorbs whatever the other columns leave of the table.
      width: widths && key === flexColumnKey ? undefined : width,
      onHeaderCell: (column) => ({
        ...(col.onHeaderCell ? col.onHeaderCell(column) : {}),
        "data-resize-key": key,
        resizeKey: key,
        resizeLabel,
        resizeActive: activeKey === key,
        resizeFocusRef: focusKeyRef,
        onResizePointerDown: handlePointerDown,
        onResizeKeyDown: handleKeyDown,
        onResizeReset: reset,
        shouldIgnoreClick,
      }),
    };
  });

  return {
    columns: resizableColumns,
    tableProps: {
      components: { header: { cell: ResizableHeaderCell } },
      ...(widths ? { tableLayout: "fixed", scroll: { x: scrollX } } : {}),
    },
    tableClassName: widths ? `${styles.resizableTable} ${styles.fixedLayout}` : styles.resizableTable,
  };
}
